import type { IntlKey } from 'locales';
import { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { isDashboardWorkspace } from 'state/quotes/infos/infos.model';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';
import { workspaceTabActivated, workspaceTabMoved, workspaceTabRenamed } from 'state/workspaces/workspace.actions';
import { workspaceTabRemovedThunk, workspaceTabsAllRemovedThunk } from 'state/workspaces/workspace.thunks';
import { workspaceHideDashboard, workspaceShowDashboard } from 'state/workspaces/workspaces/workspace.actions';
import type { ActiveQuoteDashboard } from 'state/workspaces/workspaces/workspaces.model';
import {
  getWorkspaceDashboardVisibility,
  getWorkspaceTabModelWithWorkspaceType,
} from 'state/workspaces/workspaces/workspaces.selector';

export interface TabsWorkspaceOwnProps {
  paddingClassName?: string;
  workspace: WorkspaceType;

  children?: (
    selectedTab: WorkspaceTab | null,
    isDashboardActive: ActiveQuoteDashboard,
    onHideRunningQuotes?: () => void,
  ) => ReactNode;

  onAddNewTab: undefined | (() => void);
  onImportExcel?: () => void;
  e2e?: string;

  addNewTabTooltip?: IntlKey;
}

interface StateProps {
  isDashboardActive: ActiveQuoteDashboard;

  tabs: WorkspaceTab[];
  selectedTab: WorkspaceTab | null;
}

interface DispatchProps {
  onClose: (tab: WorkspaceTab) => void;

  onRename: (tab: WorkspaceTab, newName: string) => void;

  onReset: () => void;

  setSelectedTab: (tab: WorkspaceTab) => void;

  onShowRunningQuotes?(): void;

  onHideRunningQuotes?(): void;
  moveTab: (sourceIndex: number, targetIndex: number) => void;
}

export type TabsWorkspaceProps = TabsWorkspaceOwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, TabsWorkspaceOwnProps, AppState> = (
  _initialState: AppState,
  ownProps: TabsWorkspaceOwnProps,
) => {
  const getWorkspaceTabModel = getWorkspaceTabModelWithWorkspaceType(ownProps.workspace);
  return (state, { workspace }) => {
    const { tabs, selectedTab } = getWorkspaceTabModel(state);
    const isDashboardActive = isDashboardWorkspace(workspace)
      ? getWorkspaceDashboardVisibility(state, workspace)
      : ('hide' as ActiveQuoteDashboard);
    return {
      isDashboardActive,
      tabs,
      selectedTab,
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { workspace }: TabsWorkspaceOwnProps): DispatchProps => {
  const dispatchProps = {
    onReset: () => {
      dispatch(workspaceTabsAllRemovedThunk(workspace));
    },
    onClose: ({ id }) => {
      dispatch(workspaceTabRemovedThunk(id, workspace));
    },
    onRename: ({ id }, newName) => {
      dispatch(workspaceTabRenamed(id, newName, workspace));
    },
    setSelectedTab: ({ id, type }) => {
      dispatch(workspaceTabActivated(id, workspace, type));
    },
    moveTab: (sourceIndex, targetIndex) => {
      dispatch(workspaceTabMoved(workspace, sourceIndex, targetIndex));
    },
  } satisfies DispatchProps;

  if (isDashboardWorkspace(workspace)) {
    return {
      ...dispatchProps,
      onShowRunningQuotes: () => {
        dispatch(workspaceShowDashboard(workspace));
        logAnalytics.trackEvent('Active quotes dashboard', 'Open dashboard');
      },
      onHideRunningQuotes: () => {
        dispatch(workspaceHideDashboard(workspace));
        logAnalytics.trackEvent('Active quotes dashboard', 'Close dashboard');
      },
    };
  } else {
    return dispatchProps;
  }
};

export const connectTabsWorkspace = connect(mapStateToProps, mapDispatchToProps);
